import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Global from '../../APIConfig';
import { toast } from '../../components/ui/toast';
import AuthUser from '../../AuthUser';
import { Loader2 } from 'lucide-react';
import PasswordInput from '../../components/ui/PasswordInput';

const STEP_ACCOUNT = 1;
const STEP_LOCATION = 2;

/**
 * RegisterForm — create-account form used inside the AuthScreen tab switch.
 * Two-step wizard (Account -> Location) with inline validation.
 * Submit keeps the original RegisterCustomer + OTP flow.
 */
export default function RegisterForm() {
  let navigate = useNavigate();
  const { logout } = AuthUser();
  const [step, setStep] = useState(STEP_ACCOUNT);
  const [Name, setName] = useState('');
  const [Company, setCompany] = useState('');
  const [Phone, setPhone] = useState('');
  const [Email, setEmail] = useState('');
  const [Zip, setZip] = useState('');
  const [Country, setCountry] = useState('');
  const [City, setCity] = useState('');
  const [Hub, setHub] = useState('');
  const [Password, setPassword] = useState('');
  const [ConfirmPass, setConfirmPass] = useState('');
  const [Address, setAddress] = useState('');
  const [Countrylist, setCountrylist] = useState([]);
  const [Citylist, setCitylist] = useState([]);
  const [Hublist, setHublist] = useState([]);
  const [errors, setErrors] = useState({});
  const [Subloading, setSubloading] = useState(false);

  useEffect(() => {
    GetCountrylist();
  }, []);

  const clearErr = (key) => {
    if (errors[key]) {
      const next = { ...errors };
      delete next[key];
      setErrors(next);
    }
  };

  const validateAccount = () => {
    const errs = {};
    if (!Name.trim()) errs.Name = 'Customer name is required';
    if (!Company.trim()) errs.Company = 'Company name is required';
    if (!Phone.trim()) errs.Phone = 'Phone number is required';
    else if (!/^[+()\-\s\d]{7,16}$/.test(Phone.trim())) errs.Phone = 'Enter a valid phone number';
    if (!Email.trim()) errs.Email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(Email.trim())) errs.Email = 'Enter a valid email address';
    if (!Password) errs.Password = 'Password is required';
    else if (Password.length < 6) errs.Password = 'Password must be at least 6 characters';
    if (!ConfirmPass) errs.ConfirmPass = 'Confirm your password';
    else if (Password !== ConfirmPass) errs.ConfirmPass = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateLocation = () => {
    const errs = {};
    if (!Zip.trim()) errs.Zip = 'Postal code is required';
    if (!Country) errs.Country = 'Please select a country';
    if (!City) errs.City = 'Please select a city';
    if (!Address.trim()) errs.Address = 'Address is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    if (validateAccount()) setStep(STEP_LOCATION);
  };

  const handleSubmit = () => {
    if (!validateLocation()) return;
    setSubloading(true);
    axios({
      method: 'post',
      url: Global.baseUrl + 'RegisterCustomer',
      data: {
        Name: Name,
        Company: Company,
        Phone: Phone,
        Email: Email,
        Zip: Zip,
        Country: Country,
        City: City,
        Hub: Hub,
        Address: Address,
        Password, Password,
        ConfirmPass, ConfirmPass
      }
    })
      .then(function (response) {
        if (response.data.status == 200) {
          setSubloading(false);
          toast.success(response.data.message);
          setTimeout(() => {
            navigate('/Otp?email=' + Email);
          }, 1000);
        } else {
          setSubloading(false);
          const msg = response.data.message;
          const emailErr = msg && msg.Email ? (Array.isArray(msg.Email) ? msg.Email[0] : msg.Email) : null;
          const phoneErr = msg && msg.Phone ? (Array.isArray(msg.Phone) ? msg.Phone[0] : msg.Phone) : null;
          if (emailErr) toast.warning(emailErr);
          if (phoneErr) toast.warning(phoneErr);
          if (!emailErr && !phoneErr && typeof msg === 'string') toast.warning(msg);
        }
      })
      .catch(function (error) {
        if (error.response && error.response.status == 401) {
          toast.error(error.response.statusText);
          logout();
          navigate('/');
          window.location.reload();
        } else {
          toast.error('Something went wrong. Please try again.');
        }
        setSubloading(false);
      });
  };

  const handleCountryChange = (val) => {
    setCountry(val);
    setCity('');
    setHub('');
    setCitylist([]);
    setHublist([]);
    clearErr('Country');
    if (val) GetCityList(val);
  };
  const handleCityChange = (val) => {
    setCity(val);
    setHub('');
    setHublist([]);
    clearErr('City');
    if (val) GetHubList(val);
  };

  const GetCountrylist = () => {
    axios({
      method: 'post',
      url: Global.baseUrl + 'Countrylist',
      data: {}
    })
      .then(function (response) {
        if (response.data.status == 200) {
          var data = response.data.value;
          setCountrylist(data);
        }
      })
      .catch(function (error) {
      });
  }
  const GetCityList = (val) => {
    axios({
      method: 'post',
      url: Global.baseUrl + 'Citylist',
      data: {
        name: val
      }
    })
      .then(function (response) {
        if (response.data.status == 200) {
          var data = response.data.value;
          setCitylist(data);
        }
      })
      .catch(function (error) {
      });
  }
  const GetHubList = (val) => {
    axios({
      method: 'post',
      url: Global.baseUrl + 'Hublist',
      data: {
        cityid: val
      }
    })
      .then(function (response) {
        if (response.data.status == 200) {
          var data = response.data.value;
          setHublist(data);
        }
      })
      .catch(function (error) {
      });
  }

  const field = (name, label, children, options = {}) => {
    const { required = false, full = false, error = '' } = options;
    return (
      <div className={`pxl-field${full ? ' pxl-field--full' : ''}${error ? ' pxl-field--error' : ''}`}>
        <label htmlFor={name}>{label}{required && <span className="px-req">*</span>}</label>
        {children}
        {error && <span className="pxl-err">{error}</span>}
      </div>
    );
  };

  const setErrFree = (setter, key) => (e) => { setter(e.target.value); clearErr(key); };

  return (
    <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); if (step === STEP_ACCOUNT) nextStep(); else handleSubmit(); }}>
      <div className="pxl-steps">
        <div className={`pxl-step${step >= STEP_ACCOUNT ? ' pxl-step--active' : ''}${step > STEP_ACCOUNT ? ' pxl-step--done' : ''}`}>
          <span className="pxl-step__num">1</span>
          <span className="pxl-step__label">Account</span>
        </div>
        <span className="pxl-steps__line"></span>
        <div className={`pxl-step${step >= STEP_LOCATION ? ' pxl-step--active' : ''}${step > STEP_LOCATION ? ' pxl-step--done' : ''}`}>
          <span className="pxl-step__num">2</span>
          <span className="pxl-step__label">Location</span>
        </div>
      </div>

      {step === STEP_ACCOUNT && (
        <div className="pxl-grid">
          {field('Name', 'Customer Name',
            <input id="Name" type="text" name="name" autoComplete="name" placeholder="Customer name" value={Name} onChange={setErrFree(setName, 'Name')} />,
            { required: true, error: errors.Name })}
          {field('Company', 'Company Name',
            <input id="Company" type="text" name="company" autoComplete="organization" placeholder="Customer company name" value={Company} onChange={setErrFree(setCompany, 'Company')} />,
            { required: true, error: errors.Company })}
          {field('Phone', 'Phone',
            <input id="Phone" type="tel" name="phone" inputMode="tel" autoComplete="tel" placeholder="Customer phone no." value={Phone} onChange={setErrFree(setPhone, 'Phone')} />,
            { required: true, error: errors.Phone })}
          {field('Email', 'Email',
            <input id="Email" type="email" name="email" autoComplete="email" placeholder="Customer email" value={Email} onChange={setErrFree(setEmail, 'Email')} />,
            { required: true, error: errors.Email })}
          {field('Password', 'Password',
            <PasswordInput id="Password" name="password" autoComplete="new-password" placeholder="Password" value={Password} onChange={setErrFree(setPassword, 'Password')} />,
            { required: true, error: errors.Password })}
          {field('ConfirmPass', 'Confirm Password',
            <PasswordInput id="ConfirmPass" name="confirmPassword" autoComplete="new-password" placeholder="Confirm Password" value={ConfirmPass} onChange={setErrFree(setConfirmPass, 'ConfirmPass')} />,
            { required: true, error: errors.ConfirmPass })}
        </div>
      )}

      {step === STEP_LOCATION && (
        <>
          <div className="pxl-grid">
            {field('Zip', 'Postal Code',
              <input id="Zip" type="text" name="postalCode" autoComplete="postal-code" placeholder="Customer postal code" value={Zip} onChange={setErrFree(setZip, 'Zip')} />,
              { required: true, error: errors.Zip })}
            {field('Country', 'Country',
              <select id="Country" name="country" value={Country} onChange={(e) => handleCountryChange(e.target.value)}>
                <option value="">-- Please Select --</option>
                {Countrylist.map((list, i) => (
                  <option key={i} value={list.country}>{list.country}</option>
                ))}
              </select>,
              { required: true, error: errors.Country })}
            {field('City', 'City',
              <select id="City" name="city" value={City} disabled={!Country} onChange={(e) => handleCityChange(e.target.value)}>
                <option value="">-- Please Select --</option>
                {Citylist.map((list, i) => (
                  <option key={i} value={list.value}>{list.label}</option>
                ))}
              </select>,
              { required: true, error: errors.City })}
            {field('Hub', 'Hub',
              <select id="Hub" name="hub" value={Hub} disabled={!City} onChange={(e) => { setHub(e.target.value); clearErr('Hub'); }}>
                <option value="">-- Please Select --</option>
                {Hublist.map((list, i) => (
                  <option key={i} value={list.value}>{list.label}</option>
                ))}
              </select>,
              {})}
          </div>
          {field('Address', 'Address',
            <textarea id="Address" name="address" rows={3} placeholder="Customer address" value={Address} onChange={setErrFree(setAddress, 'Address')} ></textarea>,
            { required: true, full: true, error: errors.Address })}
        </>
      )}

      <div className="pxl-btn-row">
        {step === STEP_LOCATION && (
          <button type="button" className="pxl-btn pxl-btn--back" onClick={() => setStep(STEP_ACCOUNT)}>Back</button>
        )}
        {Subloading == false ? (
          <button type="submit" className="pxl-btn">{step === STEP_ACCOUNT ? 'Continue' : 'Submit'}</button>
        ) : (
          <button type="button" className="pxl-btn" disabled><Loader2 size={16} className="px-spin" /> Submitting…</button>
        )}
      </div>
    </form>
  );
}